import { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Settings, LogOut, Gamepad2, LifeBuoy, Users } from 'lucide-react';
import {
  cn,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@dgm/ui';
import { FEATURES } from '@dgm/shared';
import { useAuth } from '../lib/auth.js';
import { OnboardingWizard } from './OnboardingWizard.js';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  // Friends nav item appears only when the feature is on (Cycle-1 SG-3).
  ...(FEATURES.friends ? [{ to: '/friends', label: 'Friends', icon: Users }] : []),
  { to: '/settings', label: 'Settings', icon: Settings },
  { to: '/help', label: 'Help', icon: LifeBuoy },
];

/**
 * Authenticated app shell: top bar + sidebar + routed content. Acts as the
 * auth guard — hydrates the session on mount and redirects to /login when
 * anonymous.
 */
export function AppShell() {
  const { status, user, pendingDeletion, onboarded, hydrate, reactivate, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (status === 'loading') void hydrate();
  }, [status, hydrate]);

  useEffect(() => {
    if (status === 'anonymous') navigate('/login', { replace: true });
  }, [status, navigate]);

  if (status !== 'authenticated') {
    return <div className="flex min-h-full items-center justify-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="grid min-h-full grid-cols-[220px_1fr]">
      <aside className="flex flex-col border-r bg-muted/20 p-4">
        <div className="mb-6 flex items-center gap-2 px-2 font-bold text-primary">
          <Gamepad2 className="h-5 w-5" /> du-game-maker
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
                location.pathname.startsWith(to) && 'bg-accent text-accent-foreground',
              )}
            >
              <Icon className="h-4 w-4" /> {label}
            </Link>
          ))}
        </nav>
      </aside>

      <div className="flex flex-col">
        <header className="flex h-14 items-center justify-end border-b px-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                {user?.displayName ?? user?.email}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  navigate('/login', { replace: true });
                }}
              >
                <LogOut className="h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        {pendingDeletion ? (
          <div className="flex items-center justify-between gap-4 border-b border-destructive/30 bg-destructive/10 px-6 py-3 text-sm">
            <span className="text-destructive">
              Your account is scheduled for deletion. Reactivate to keep it.
            </span>
            <Button size="sm" variant="outline" onClick={() => void reactivate()}>
              Reactivate account
            </Button>
          </div>
        ) : null}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
        {FEATURES.onboarding && !onboarded ? <OnboardingWizard /> : null}
      </div>
    </div>
  );
}
